const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  
  const config: RequestInit = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(url, config);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.error || 'Request failed');
  }
  
  return response.json();
}

// Session API
export const sessionApi = {
  getCurrent: () => request<import('../types').Session | null>('/session/current'),
  
  updateStatus: (status: string) =>
    request<import('../types').Session>('/session/status', {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  
  updateTime: (elapsedMs: number) =>
    request<{ success: boolean }>('/session/time', {
      method: 'PATCH',
      body: JSON.stringify({ elapsed_ms: elapsedMs }),
    }),
  
  end: () =>
    request<{ success: boolean }>('/session/end', {
      method: 'POST',
    }),
};

// Assessment API
export const assessmentApi = {
  getQuestions: () =>
    request<import('../types').Question[]>('/assessment/questions'),
  
  getQuestion: (questionId: string) =>
    request<import('../types').Question>(`/assessment/questions/${questionId}`),
  
  submitAnswer: (questionId: string, answer: string, startTime: string, endTime: string) =>
    request<import('../types').AnswerResult>('/assessment/submit', {
      method: 'POST',
      body: JSON.stringify({
        question_id: questionId,
        answer,
        start_time: startTime,
        end_time: endTime,
      }),
    }),
  
  submitFollowup: (questionId: string, choiceIds: string[], freeResponseAnswer?: string) =>
    request<{ success: boolean }>('/assessment/followup', {
      method: 'POST',
      body: JSON.stringify({
        question_id: questionId,
        choice_ids: choiceIds,
        free_response_answer: freeResponseAnswer,
      }),
    }),
  
  skipQuestion: (questionId: string, reason?: string) =>
    request<{ success: boolean }>('/assessment/skip', {
      method: 'POST',
      body: JSON.stringify({ question_id: questionId, reason }),
    }),
  
  getSkippedQuestions: () =>
    request<import('../types').Question[]>('/assessment/skipped'),
  
  submitSkipReason: (questionId: string, reason: string) =>
    request<{ success: boolean }>('/assessment/skip-reason', {
      method: 'POST',
      body: JSON.stringify({ question_id: questionId, reason }),
    }),
  
  getProgress: () =>
    request<import('../types').AssessmentProgress>('/assessment/progress'),
};

// Survey API
export const surveyApi = {
  getDefinition: (type: 'pre' | 'post' | 'midterm' | 'endterm') =>
    request<import('../types').SurveyDefinition>(`/survey/definition/${type}`),
  
  submitPreSurvey: (responses: import('../types').PreSurveyResponses) =>
    request<{ success: boolean }>('/survey/pre', {
      method: 'POST',
      body: JSON.stringify({ responses }),
    }),
  
  submitPostSurvey: (responses: import('../types').PostSurveyResponses) =>
    request<{ success: boolean }>('/survey/post', {
      method: 'POST',
      body: JSON.stringify({ responses }),
    }),
  
  submitMidSurvey: (responses: object) =>
    request<{ success: boolean }>('/survey/mid', {
      method: 'POST',
      body: JSON.stringify({ responses }),
    }),
  
  submitEndSurvey: (responses: object) =>
    request<{ success: boolean }>('/survey/end', {
      method: 'POST',
      body: JSON.stringify({ responses }),
    }),

  getConfig: () =>
    request<{ mid_semester_date: string; end_semester_date: string }>('/survey/config'),

  getStatus: () =>
    request<Record<string, boolean>>('/survey/status'),

  getSurveyTypes: () =>
    request<{ survey_types: string[] }>('/survey/for-assessment'),
};

// Feedback API
export const feedbackApi = {
  getSummary: () =>
    request<import('../types').SessionSummary>('/feedback/summary'),
  
  getTeaser: () =>
    request<import('../types').FeedbackTeaser>('/feedback/teaser'),
};

// Content API
export const contentApi = {
  getDirections: () =>
    request<import('../types').DirectionsContent>('/content/directions'),
  
  getScratchReminder: () =>
    request<{
      title: string;
      message: string;
      explanation: string;
      email: string;
      subject: string;
      button_text: string;
    }>('/content/scratch-reminder'),
};

// Auth API
export const authApi = {
  getInstitutions: () =>
    request<{ institutions: import('../types').InstitutionOption[] }>('/auth/institutions'),

  register: (payload: {
    role: 'student' | 'educator';
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    institution_id: string;
  }) =>
    request<{
      user: import('../types').PortalUser;
      pending_approval?: boolean;
      message?: string;
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  login: (email: string, password: string) =>
    request<{ user: import('../types').PortalUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  me: () =>
    request<{ authenticated: boolean; user?: import('../types').PortalUser }>('/auth/me'),

  updateProfile: (payload: { first_name: string; last_name: string; email: string }) =>
    request<{ user: import('../types').PortalUser }>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  updatePassword: (payload: { current_password: string; new_password: string }) =>
    request<{ success: boolean }>('/auth/password', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  logout: () =>
    request<{ success: boolean }>('/auth/logout', {
      method: 'POST',
    }),
};

function educatorPortalQuery(options?: { educatorUserId?: string; assessmentId?: string }) {
  const params = new URLSearchParams();

  if (options?.educatorUserId) {
    params.set('educator_user_id', options.educatorUserId);
  }

  if (options?.assessmentId) {
    params.set('assessment_id', options.assessmentId);
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

// Portal API
export const portalApi = {
  getStudentCurrentClasses: () =>
    request<import('../types').PortalClass[]>('/portal/student/classes/current'),

  getStudentPastClasses: () =>
    request<import('../types').PortalClass[]>('/portal/student/classes/past'),

  getStudentAvailableClasses: () =>
    request<import('../types').PortalClass[]>('/portal/student/classes/available'),

  enrollInClass: (sectionId: string) =>
    request<{ success: boolean }>('/portal/student/enroll', {
      method: 'POST',
      body: JSON.stringify({ section_id: sectionId }),
    }),

  startStudentClassAssessment: (sectionId: string, assessmentId?: string) =>
    request<{ success: boolean; session_id: string; assessment_id: string }>(
      `/portal/student/classes/${sectionId}/start-assessment`,
      {
        method: 'POST',
        body: JSON.stringify(assessmentId ? { assessment_id: assessmentId } : {}),
      }
    ),

  getStudentClassDetail: (sectionId: string) =>
    request<import('../types').StudentClassDetail>(`/portal/student/classes/${sectionId}`),

  getStudentClassFeedback: (sectionId: string, sessionId: string) =>
    request<import('../types').StudentAssessmentFeedback>(
      `/portal/student/classes/${sectionId}/feedback/${sessionId}`
    ),

  markStudentFeedbackResourceVisited: (sectionId: string, sessionId: string, resourceId: string) =>
    request<{ success: boolean; visited: boolean }>(
      `/portal/student/classes/${sectionId}/feedback/${sessionId}/resources/${resourceId}/visit`,
      {
        method: 'POST',
      }
    ),

  getFacultyCurrentClasses: (educatorUserId?: string) =>
    request<import('../types').PortalClass[]>(
      `/portal/faculty/classes/current${educatorPortalQuery({ educatorUserId })}`
    ),

  getFacultyAssessments: () =>
    request<import('../types').AssessmentOption[]>('/portal/faculty/assessments'),

  getFacultyPastClasses: (educatorUserId?: string) =>
    request<import('../types').PortalClass[]>(
      `/portal/faculty/classes/past${educatorPortalQuery({ educatorUserId })}`
    ),

  getFacultyClassDetail: (sectionId: string, educatorUserId?: string) =>
    request<import('../types').FacultyClassDetail>(
      `/portal/faculty/classes/${sectionId}${educatorPortalQuery({ educatorUserId })}`
    ),

  createFacultyClass: (payload: {
    term: string;
    class_name: string;
    educator_user_id?: string;
    course_code: string;
    section_code: string;
    start_date?: string;
    end_date?: string;
  }) =>
    request<{ success: boolean; section_id: string }>('/portal/faculty/classes', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateFacultyClass: (sectionId: string, payload: {
    term: string;
    class_name: string;
    institution?: string;
    course_code: string;
    section_code: string;
    start_date?: string;
    end_date?: string;
  }) =>
    request<{ success: boolean }>(`/portal/faculty/classes/${sectionId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  addFacultyClassAssessment: (sectionId: string, payload: {
    assessment_id: string;
    opens_at?: string;
    closes_at?: string;
    schedule_timezone?: string;
    include_pre_survey?: boolean;
    include_post_survey?: boolean;
  }, educatorUserId?: string) =>
    request<{ success: boolean }>(
      `/portal/faculty/classes/${sectionId}/assessments${educatorPortalQuery({ educatorUserId })}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    ),

  getFacultyClassDashboard: (sectionId: string, educatorUserId?: string) =>
    request<import('../types').FacultyClassDashboard>(
      `/portal/faculty/classes/${sectionId}/dashboard${educatorPortalQuery({ educatorUserId })}`
    ),

  getFacultyClassStudents: (sectionId: string, educatorUserId?: string, assessmentId?: string) =>
    request<import('../types').FacultyClassStudent[]>(
      `/portal/faculty/classes/${sectionId}/students${educatorPortalQuery({ educatorUserId, assessmentId })}`
    ),

  getFacultyClassQuestionAnalysis: (sectionId: string, assessmentId: string, educatorUserId?: string) =>
    request<import('../types').FacultyClassQuestionAnalysisItem[]>(
      `/portal/faculty/classes/${sectionId}/question-analysis${educatorPortalQuery({ educatorUserId, assessmentId })}`
    ),

  getFacultyStudentProfile: (
    sectionId: string,
    studentId: string,
    educatorUserId?: string,
    assessmentId?: string
  ) =>
    request<import('../types').FacultyStudentProfile>(
      `/portal/faculty/classes/${sectionId}/students/${studentId}/profile${educatorPortalQuery({
        educatorUserId,
        assessmentId,
      })}`
    ),

  getAdminEducators: () =>
    request<import('../types').AdminEducator[]>('/portal/admin/educators'),

  createAdminEducator: (payload: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
  }) =>
    request<import('../types').AdminEducator>('/portal/admin/educators', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  approveAdminEducator: (educatorUserId: string) =>
    request<{ success: boolean }>(`/portal/admin/educators/${educatorUserId}/approve`, {
      method: 'PATCH',
    }),

  removeAdminEducator: (educatorUserId: string) =>
    request<{ success: boolean }>(`/portal/admin/educators/${educatorUserId}`, {
      method: 'DELETE',
    }),
};

export { ApiError };
